/**
 * @file scheduledMeetingsApi.ts
 * @brief Boardroom scheduling client — `/api/meetings` (mobile My Meetings parity).
 *
 * @author DES
 * @date 28-07-2026
 */

import { z } from "zod";
import { apiDelete, apiGet, apiPost, apiPut } from "@/shared/api/client";
import { requestScheduleSync } from "@/features/scheduleReminders/syncSignal";
import { meetingsEndpoints } from "@/shared/api/endpoints";

const ParticipantSchema = z
  .object({
    id: z.string().optional().nullable(),
    userId: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
    name: z.string().optional().nullable(),
    isExternal: z.boolean().optional(),
    profileImageUrl: z.string().optional().nullable(),
    shared: z.boolean().optional(),
    // B48: an account behind the person; B45: on the roster, access taken
    // back (never shown in Edit meeting, never sent back).
    hasAccount: z.boolean().optional().nullable(),
    accessRemoved: z.boolean().optional().nullable(),
  })
  .passthrough();

/** B48: what the organizer may change now (BACKEND meetings/editWindow.mjs). */
export const EditableSchema = z
  .object({
    title: z.boolean().default(false),
    participants: z.boolean().default(false),
    agenda: z.boolean().default(false),
    time: z.boolean().default(false),
    languages: z.boolean().default(false),
  })
  .passthrough();
export type MeetingEditable = z.infer<typeof EditableSchema>;
export type MeetingEditPhase = "before" | "during" | "after" | "cancelled";

export const ScheduledMeetingSchema = z
  .object({
    id: z.string(),
    company_id: z.string().nullable().optional(),
    organizer_id: z.string(),
    organizer_name: z.string().optional().nullable(),
    organizer_email: z.string().optional().nullable(),
    title: z.string(),
    agenda: z.string().nullable().optional(),
    target_languages: z.array(z.string()).default([]),
    scheduled_at: z.string(),
    duration_minutes: z.number().nullable().optional(),
    status: z.enum(["scheduled", "started", "completed", "cancelled"]),
    boardroom_meeting_id: z.string().nullable().optional(),
    participants: z.array(ParticipantSchema).default([]),
    // Agenda notes: only the organiser gets them back (backend 25/28-09-2026).
    prep_note_id: z.string().nullable().optional(),
    prep_note_ids: z.array(z.string()).nullable().optional(),
    prep_notes: z
      .array(z.object({ id: z.string(), title: z.string().nullable().optional() }).passthrough())
      .nullable()
      .optional(),
    // B48, organizer rows only (absent on an older server: today's rules).
    edit_phase: z.enum(["before", "during", "after", "cancelled"]).nullable().optional(),
    editable: EditableSchema.nullable().optional(),
    is_organizer: z.boolean().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export type ScheduledMeeting = z.infer<typeof ScheduledMeetingSchema>;
export type ScheduledMeetingParticipant = z.infer<typeof ParticipantSchema>;

export type MeetingScheduleInput = {
  title: string;
  agenda?: string;
  targetLanguages?: string[];
  scheduledAt: string;
  durationMinutes?: number;
  /** Agenda notes (same fields as the phone sends; first id for an old server). */
  prepNoteIds?: string[];
  prepNoteId?: string | null;
  participants?: Array<{
    userId?: string;
    email?: string;
    name?: string;
    isExternal?: boolean;
    shared?: boolean;
    /** B48: an entry echoed back as the server sent it. */
    [key: string]: unknown;
  }>;
};

/** B48: a refusal of PUT /api/meetings/:id the sheet explains in words. */
export function meetingEditErrorMessage(err: unknown): string | null {
  const e = err as { code?: string; message?: string } | null;
  switch (e?.code) {
    // The server's own sentence names the field ("The agenda is locked…").
    case "MEETING_FIELD_LOCKED":
      return e.message || "Part of this meeting can no longer change.";
    case "MEETING_LOCKED":
      return "A cancelled meeting can't be edited.";
    case "EMAIL_REQUIRED":
      return "Someone new needs an email.";
    case "EMAIL_INVALID":
      return "One of the emails doesn't look right.";
    case "NAME_REQUIRED":
      return "Someone new needs a name.";
    case "NAME_TOO_LONG":
      return "One of the names is too long.";
    default:
      return null;
  }
}

const ListSchema = z.object({
  meetings: z.array(ScheduledMeetingSchema).default([]),
});

const OneSchema = z.object({
  meeting: ScheduledMeetingSchema,
});

export async function listScheduledMeetings(
  scope: "upcoming" | "past" | "all" = "all",
): Promise<ScheduledMeeting[]> {
  const raw = await apiGet<unknown>(
    `${meetingsEndpoints.list}?scope=${encodeURIComponent(scope)}`,
  );
  return ListSchema.parse(raw).meetings;
}

export async function createScheduledMeeting(
  input: MeetingScheduleInput,
): Promise<ScheduledMeeting> {
  const raw = await apiPost<unknown>(meetingsEndpoints.list, input);
  // Desktop app: its alarm + calendar event (09-10-2026). No-op in a browser.
  requestScheduleSync();
  return OneSchema.parse(raw).meeting;
}

export async function updateScheduledMeeting(
  id: string,
  input: Partial<MeetingScheduleInput>,
): Promise<ScheduledMeeting> {
  const raw = await apiPut<unknown>(meetingsEndpoints.byId(id), input);
  requestScheduleSync();
  return OneSchema.parse(raw).meeting;
}

export async function cancelScheduledMeeting(id: string): Promise<void> {
  await apiDelete(meetingsEndpoints.byId(id));
  requestScheduleSync();
}
